import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import type Konva from 'konva';
import type { CanvasPlugin } from '../../../core/canvas/CanvasPlugin';
import type { CommandManager } from '../../../core/commands/CommandManager';
import { UpdateNodeTextCommand } from '../../../core/commands/UpdateNodeTextCommand';
import { UpdateTableCellTextCommand } from '../../../core/commands/UpdateTableCellTextCommand';
import { UpdateEdgeCommand } from '../../../core/commands/edges/UpdateEdgeCommand';
import type { EventBus } from '../../../core/events/IEventBus';
import type { Edge, Node } from '../../../core/models/scene';
import type { DiagramType } from '../../../core/tools/interfaces/ITool';
import type { ToolRegistry } from '../../../core/tools/ToolRegistry';
import { captureFlowchartTextLayout } from '../../../domains/diagram/shapes/flowchartTextLayout';
import { useTableCellStore, type TableCellKey } from '../../../stores/tableCellStore';
import { useDiagramStore } from '../../../stores/diagramStore';
import { Logger } from '../../../utils/logger';
import {
  getOrgChartDefaultDepartmentName,
  getOrgChartDefaultPersonName,
  getOrgChartDefaultPositionTitle,
} from '../../../domains/orgchart/models/defaults';

const canvasLogger = Logger.getLoggerWithPrefix('DiagramCanvasWithTransform');

type EditingTextState =
  | {
      nodeId?: string;
      edgeId?: string;
      field?: 'name' | 'location' | 'text';
      tableCellKey?: TableCellKey;
      initialTextOverride?: string;
      selectAllOnMount?: boolean;
      textNode: Konva.Text;
    }
  | null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function resolveNodeTextFieldValue(
  node: Node | undefined,
  field: 'name' | 'location' | 'text' | undefined,
): string {
  if (!node || !field) return '';

  const nodeRecord = node as Node & Record<string, unknown>;
  const attrs = isRecord(nodeRecord.attrs) ? nodeRecord.attrs : {};
  const props = isRecord(nodeRecord.props) ? nodeRecord.props : {};

  if (field !== 'text') {
    const topLevelValue = nodeRecord[field];
    if (typeof topLevelValue === 'string') return topLevelValue;
    const attrsValue = attrs[field];
    if (typeof attrsValue === 'string') return attrsValue;
    const propsValue = props[field];
    if (typeof propsValue === 'string') return propsValue;
    return '';
  }

  const topLevelText = nodeRecord.text;
  const attrsLabel = attrs.label;

  if (node.type === 'basic-shape') {
    if (typeof topLevelText === 'string') return topLevelText;
    if (typeof attrsLabel === 'string' && attrsLabel.length > 0) return attrsLabel;
    if (typeof attrs.text === 'string') return attrs.text;
    return '';
  }

  if (node.type === 'flowchart-shape' || node.type === 'bpmn-shape') {
    if (typeof topLevelText === 'string' && topLevelText.trim().length > 0) {
      return topLevelText;
    }
    return typeof attrsLabel === 'string' ? attrsLabel : '';
  }

  if (node.type === 'org-shape') {
    return typeof topLevelText === 'string'
      ? topLevelText
      : typeof attrsLabel === 'string'
        ? attrsLabel
        : '';
  }

  if (typeof attrs.text === 'string') return attrs.text;
  return typeof topLevelText === 'string' ? topLevelText : '';
}

const MANIFEST_CARD_MIN_HEIGHT = 60;
const MANIFEST_CARD_ICON_SIZE = 32;
const MANIFEST_CARD_VERTICAL_PADDING = 12;
const MANIFEST_CARD_TEXT_GAP = 3;
const MANIFEST_CARD_SECONDARY_HEIGHT = 12;
const MANIFEST_CARD_TEXT_X = 52;
const MANIFEST_CARD_TEXT_RIGHT_PADDING = 10;
const MANIFEST_CARD_NAME_FONT_SIZE = 12;
const MANIFEST_CARD_NAME_LINE_HEIGHT = 1.15;
const MANIFEST_CARD_AVERAGE_CHAR_WIDTH = 0.58;

function getNumericNodeDimension(node: Node, keys: Array<'width' | 'height' | 'w' | 'h'>, fallback: number): number {
  const nodeRecord = node as Node & Record<string, unknown>;
  for (const key of keys) {
    const value = nodeRecord[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return fallback;
}

function isManifestResourceCardNameEdit(node: Node, field: 'name' | 'location' | 'text' | undefined): boolean {
  if (field !== 'name') return false;

  const nodeRecord = node as Node & Record<string, unknown>;
  const attrs = isRecord(nodeRecord.attrs) ? nodeRecord.attrs : {};
  const width = getNumericNodeDimension(node, ['width', 'w'], 180);
  const height = getNumericNodeDimension(node, ['height', 'h'], MANIFEST_CARD_MIN_HEIGHT);
  if (width >= 300 && height >= 150) return false;

  const type = typeof nodeRecord.type === 'string' ? nodeRecord.type : '';
  const manifestId = typeof attrs._manifestId === 'string' ? attrs._manifestId : type;
  return (
    typeof attrs.resourceType === 'string' ||
    typeof attrs.iconUrl === 'string' ||
    /^(aws|azure|gcp|k8s|generic|basic)_/.test(manifestId)
  );
}

function estimateManifestCardNameTextHeight(text: string, maxWidth: number): number {
  const charsPerLine = Math.max(
    1,
    Math.floor(maxWidth / (MANIFEST_CARD_NAME_FONT_SIZE * MANIFEST_CARD_AVERAGE_CHAR_WIDTH)),
  );
  const lineCount = text.split(/\r?\n/).reduce((count, rawLine) => {
    const words = rawLine.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return count + 1;

    let lineCountForSegment = 1;
    let usedChars = 0;
    for (const word of words) {
      if (word.length > charsPerLine) {
        if (usedChars > 0) {
          lineCountForSegment += 1;
          usedChars = 0;
        }
        lineCountForSegment += Math.floor((word.length - 1) / charsPerLine);
        usedChars = ((word.length - 1) % charsPerLine) + 1;
        continue;
      }
      if (usedChars === 0) {
        usedChars = word.length;
      } else if (usedChars + 1 + word.length <= charsPerLine) {
        usedChars += 1 + word.length;
      } else {
        lineCountForSegment += 1;
        usedChars = word.length;
      }
    }

    return count + lineCountForSegment;
  }, 0);
  return Math.ceil(lineCount * MANIFEST_CARD_NAME_FONT_SIZE * MANIFEST_CARD_NAME_LINE_HEIGHT);
}

function computeManifestCardHeightForText(node: Node, text: string, measuredTextHeight: number): number {
  const width = getNumericNodeDimension(node, ['width', 'w'], 180);
  const textWidth = Math.max(24, width - MANIFEST_CARD_TEXT_X - MANIFEST_CARD_TEXT_RIGHT_PADDING);
  const textHeight = Math.max(measuredTextHeight, estimateManifestCardNameTextHeight(text, textWidth));
  return Math.max(
    MANIFEST_CARD_MIN_HEIGHT,
    MANIFEST_CARD_ICON_SIZE + MANIFEST_CARD_VERTICAL_PADDING * 2,
    Math.ceil(textHeight + MANIFEST_CARD_TEXT_GAP + MANIFEST_CARD_SECONDARY_HEIGHT + MANIFEST_CARD_VERTICAL_PADDING * 2),
  );
}

export function useCanvasTextEditing(input: {
  checkWritePermission: () => boolean;
  nodes: Node[];
  edges: Edge[];
  selectedIds: Set<string>;
  nodeRefs: MutableRefObject<Map<string, Konva.Node>>;
  edgeRefs: MutableRefObject<Map<string, Konva.Node>>;
  eventBus: EventBus;
  toolRegistry: ToolRegistry;
  commandManager: CommandManager;
  effectiveDiagramType: DiagramType;
  doc: unknown;
  canvasPluginsRef: MutableRefObject<CanvasPlugin[]>;
  getOrgDeps: () => any;
}): {
  editingTextState: EditingTextState;
  handleTextEdit: (
    id: string,
    field: 'name' | 'location' | 'text' | undefined,
    newText: string,
    metrics?: { width: number; height: number },
  ) => void;
  handleStartTextEdit: (nodeId: string, field: 'name' | 'location' | 'text', textNode: Konva.Text) => void;
  handleStartTableCellTextEdit: (nodeId: string, tableCellKey: TableCellKey, textNode: Konva.Text) => void;
  handleStartEdgeTextEdit: (edgeId: string, textNode: Konva.Text) => void;
  handleCloseTextEdit: () => void;
  getInitialEditText: () => string;
  pendingEdgeEditId: string | null;
} {
  const {
    checkWritePermission,
    nodes,
    edges,
    selectedIds,
    nodeRefs,
    edgeRefs,
    eventBus,
    toolRegistry,
    commandManager,
    effectiveDiagramType,
    doc,
    canvasPluginsRef,
    getOrgDeps,
  } = input;

  const [editingTextState, setEditingTextState] = useState<EditingTextState>(null);
  const editingTextStateRef = useRef(editingTextState);
  useEffect(() => {
    editingTextStateRef.current = editingTextState;
  }, [editingTextState]);

  const handleTextEdit = (
    id: string,
    field: 'name' | 'location' | 'text' | undefined,
    newText: string,
    metrics?: { width: number; height: number },
  ) => {
    if (editingTextState?.nodeId) {
      // Node text editing
      const node = nodes.find((n) => n.id === id);
      if (!node || !field) return;

      // Handle table cell text editing
      if (editingTextState.nodeId === id && editingTextState.tableCellKey) {
        const attrs = (node.attrs ?? {}) as Record<string, unknown>;
        const cellText = (attrs.cellText as Record<string, string> | undefined) ?? {};
        const oldEntry = cellText[editingTextState.tableCellKey];
        const oldText = oldEntry ?? '';
        if (newText !== oldText) {
          const cmd = new UpdateTableCellTextCommand({
            nodeId: id,
            cellKey: editingTextState.tableCellKey,
            newText,
            oldText: oldEntry,
          });
          commandManager.execute(cmd);
        }
        return;
      }

      {
        const context = { toolRegistry, diagramType: effectiveDiagramType, commandManager, doc };
        for (const plugin of canvasPluginsRef.current) {
          const handled = plugin.commitTextEdit?.({
            node,
            field,
            newText,
            context,
            extra: { orgDeps: getOrgDeps() },
          });
          if (handled) return;
        }
      }

      const oldText = resolveNodeTextFieldValue(node, field);
      if (newText !== oldText) {
        const commandParams: {
          nodeId: string;
          field: string;
          newText: string;
          oldText: string;
          newHeight?: number;
          oldHeight?: number;
          newFlowchartTextLayout?: Record<string, unknown> | null;
          oldFlowchartTextLayout?: Record<string, unknown> | null;
        } = {
          nodeId: id,
          field,
          newText,
          oldText,
        };

        if (
          (node.type === 'text' || node.type === 'sticky-note') &&
          field === 'text' &&
          metrics &&
          typeof metrics.height === 'number' &&
          !Number.isNaN(metrics.height)
        ) {
          const currentHeight =
            typeof (node as any).height === 'number'
              ? (node as any).height
              : typeof (node as any).h === 'number'
                ? (node as any).h
                : node.type === 'sticky-note'
                  ? 150
                  : 30;
          // For sticky notes, add 20px padding (10px top + 10px bottom)
          const padding = node.type === 'sticky-note' ? 20 : 0;
          const desiredHeight = Math.max(node.type === 'sticky-note' ? 150 : 18, Math.ceil(metrics.height + padding + 4));
          // Update height if it changed (both increase and decrease)
          if (Math.abs(desiredHeight - currentHeight) > 0.5) {
            commandParams.newHeight = desiredHeight;
            commandParams.oldHeight = currentHeight;
          }
        }

        if (
          node.type === 'flowchart-shape' &&
          field === 'text' &&
          metrics &&
          typeof metrics.height === 'number' &&
          !Number.isNaN(metrics.height)
        ) {
          const layout = captureFlowchartTextLayout(editingTextState.textNode, newText);
          const currentLayout = (node.attrs as Record<string, unknown> | undefined)?.flowchartTextLayout;
          commandParams.newFlowchartTextLayout = layout as unknown as Record<string, unknown> | null;
          commandParams.oldFlowchartTextLayout =
            currentLayout && typeof currentLayout === 'object' && !Array.isArray(currentLayout)
              ? currentLayout as Record<string, unknown>
              : null;
          const currentHeight =
            typeof (node as any).height === 'number'
              ? (node as any).height
              : typeof (node as any).h === 'number'
                ? (node as any).h
                : 80;
          // IMPORTANT: never force a hard minimum like 80px here.
          // We only want to grow when the committed text truly needs more vertical space (i.e., wrapped/multiline),
          // otherwise committing a single character can jump the node height to the previous hard min.
          const fontSize =
            typeof (node as any)?.attrs?.fontSize === 'number'
              ? ((node as any).attrs.fontSize as number)
              : typeof (node as any)?.fontSize === 'number'
                ? ((node as any).fontSize as number)
                : 12;
          // Calculate a conservative single-line baseline (accounts for line-height and textarea padding)
          const singleLineBaseline = Math.max(18, Math.ceil(fontSize * 1.5));
          const measuredInnerH = Math.ceil(metrics.height);
          const currentInnerH = Math.max(0, currentHeight - 24); // 12px padding top + bottom

          // Only grow if:
          // 1. The measured height is clearly multi-line (> 1.5x baseline), AND
          // 2. The measured height exceeds the current inner height
          // This prevents single-letter commits from unnecessarily growing the node.
          const isMultiline = measuredInnerH > singleLineBaseline * 1.5;
          if (isMultiline && measuredInnerH > currentInnerH + 0.5) {
            const desiredHeight = Math.max(20, Math.ceil(measuredInnerH + 24 + 4));
            if (desiredHeight > currentHeight + 0.5) {
              commandParams.newHeight = desiredHeight;
              commandParams.oldHeight = currentHeight;
            }
          }
        }

        if (
          node.type === 'basic-shape' &&
          field === 'text' &&
          metrics &&
          typeof metrics.height === 'number' &&
          !Number.isNaN(metrics.height)
        ) {
          const currentHeight =
            typeof (node as any).height === 'number'
              ? (node as any).height
              : typeof (node as any).h === 'number'
                ? (node as any).h
                : 80;
          const measuredInnerH = Math.ceil(metrics.height);
          const currentInnerH = Math.max(0, currentHeight - 24);
          if (measuredInnerH > currentInnerH + 0.5) {
            const desiredHeight = Math.max(20, Math.ceil(measuredInnerH + 24 + 4));
            if (desiredHeight > currentHeight + 0.5) {
              commandParams.newHeight = desiredHeight;
              commandParams.oldHeight = currentHeight;
            }
          }
        }

        if (
          isManifestResourceCardNameEdit(node, field) &&
          metrics &&
          typeof metrics.height === 'number' &&
          !Number.isNaN(metrics.height)
        ) {
          const currentHeight = getNumericNodeDimension(node, ['height', 'h'], MANIFEST_CARD_MIN_HEIGHT);
          const desiredHeight = computeManifestCardHeightForText(node, newText, metrics.height);
          if (Math.abs(desiredHeight - currentHeight) > 0.5) {
            commandParams.newHeight = desiredHeight;
            commandParams.oldHeight = currentHeight;
          }
        }

        const command = new UpdateNodeTextCommand(commandParams);
        commandManager.execute(command);
      }
    } else if (editingTextState?.edgeId) {
      // Edge text editing
      const edge = edges.find((e) => e.id === id);
      if (!edge) return;

      const oldText = (edge.attrs?.label as string | undefined) || '';
      if (newText !== oldText) {
        const trimmed = newText.trim();
        const shouldHideCaption = trimmed.length === 0;
        // Use command (writes to Yjs first) to avoid race with projection overwrites
        const cmd = new UpdateEdgeCommand(id, {
          attrs: {
            ...(edge.attrs || {}),
            label: shouldHideCaption ? undefined : newText,
            __autoLabel: false,
            __captionHidden: shouldHideCaption ? true : false,
          } as any,
        } as any);
        commandManager.execute(cmd);
      }
    }
  };

  const handleStartTextEdit = (nodeId: string, field: 'name' | 'location' | 'text', textNode: Konva.Text) => {
    // Check write permission first
    if (!checkWritePermission()) {
      return;
    }

    const node = nodes.find((n) => n.id === nodeId) as any;
    const isLocked = !!(node?.locked || node?.attrs?.locked);
    if (isLocked) return;
    setEditingTextState({ nodeId, field, textNode });
  };

  const handleStartTableCellTextEdit = useCallback(
    (nodeId: string, tableCellKey: TableCellKey, textNode: Konva.Text) => {
      if (editingTextStateRef.current) return;
      if (!checkWritePermission()) return;
      const node = nodes.find((n) => n.id === nodeId) as any;
      if (!node || node.type !== 'table') return;
      const isLocked = !!(node?.locked || node?.attrs?.locked);
      if (isLocked) return;
      setEditingTextState({ nodeId, field: 'text', tableCellKey, textNode });
    },
    [checkWritePermission, nodes],
  );

  const isTextInputFocused = () => {
    try {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      if ((el as any).isContentEditable) return true;
      return false;
    } catch {
      return false;
    }
  };

  const getTableCellTextNode = (nodeId: string, cellKey: TableCellKey): Konva.Text | null => {
    const group = nodeRefs.current.get(nodeId) as any;
    if (!group || typeof group.findOne !== 'function') return null;
    const safeId = String(nodeId).replace(/[^a-zA-Z0-9_-]/g, '_');
    const [rStr, cStr] = cellKey.split(',');
    const r = Number(rStr);
    const c = Number(cStr);
    if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
    const id = `table-${safeId}-cell-${r}-${c}`;
    const found = group.findOne(`#${id}`) as any;
    return found && found.className === 'Text' ? (found as Konva.Text) : null;
  };

  const startEditSelectedTableCell = (opts?: { initialTextOverride?: string; selectAllOnMount?: boolean }) => {
    if (editingTextStateRef.current) return;
    if (isTextInputFocused()) return;

    const currentSelectedIds = useDiagramStore.getState().selectedIds;
    if (currentSelectedIds.size !== 1) return;
    const nodeId = Array.from(currentSelectedIds)[0];
    const node = useDiagramStore.getState().nodes.find((n) => n.id === nodeId) as any;
    if (!node || node.type !== 'table') return;
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const rowCount = typeof attrs.rowCount === 'number' ? attrs.rowCount : 3;
    const colCount = typeof attrs.columnCount === 'number' ? attrs.columnCount : 3;
    const showHeader = attrs.showHeader !== false;

    const selection = useTableCellStore.getState().selections[nodeId];
    const baseKey = (selection?.anchorKey ?? selection?.selectedKeys?.[0] ?? `${showHeader ? 1 : 0},0`) as TableCellKey;

    const totalRows = Math.max(1, Math.floor(rowCount) + (showHeader ? 1 : 0));
    const totalCols = Math.max(1, Math.floor(colCount));
    const [rStr, cStr] = baseKey.split(',');
    let r = Number(rStr);
    let c = Number(cStr);
    if (!Number.isFinite(r) || !Number.isFinite(c)) {
      r = showHeader ? 1 : 0;
      c = 0;
    }
    r = Math.max(0, Math.min(totalRows - 1, r));
    c = Math.max(0, Math.min(totalCols - 1, c));
    const cellKey = `${r},${c}` as TableCellKey;

    const textNode = getTableCellTextNode(nodeId, cellKey);
    if (!textNode) return;
    if (!checkWritePermission()) return;
    const isLocked = !!(node?.locked || node?.attrs?.locked);
    if (isLocked) return;

    setEditingTextState({
      nodeId,
      field: 'text',
      tableCellKey: cellKey,
      textNode,
      initialTextOverride: opts?.initialTextOverride,
      selectAllOnMount: opts?.selectAllOnMount,
    });
  };

  const startEditSelectedTableCellRef = useRef(startEditSelectedTableCell);
  useEffect(() => {
    startEditSelectedTableCellRef.current = startEditSelectedTableCell;
  }, [startEditSelectedTableCell]);

  useEffect(() => {
    const onWindowKeyDown = (e: KeyboardEvent) => {
      if (editingTextStateRef.current) return;
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (typeof e.key !== 'string' || e.key.length !== 1) return;
      if (e.key === ' ') return;
      if (isTextInputFocused()) return;

      const currentSelectedIds = useDiagramStore.getState().selectedIds;
      if (currentSelectedIds.size !== 1) return;
      const nodeId = Array.from(currentSelectedIds)[0];
      const node = useDiagramStore.getState().nodes.find((n) => n.id === nodeId) as any;
      if (!node || node.type !== 'table') return;

      e.preventDefault();
      e.stopPropagation();
      startEditSelectedTableCellRef.current({ initialTextOverride: e.key, selectAllOnMount: false });
    };
    window.addEventListener('keydown', onWindowKeyDown, true);
    return () => window.removeEventListener('keydown', onWindowKeyDown, true);
  }, []);

  const handleStartEdgeTextEdit = useCallback(
    (edgeId: string, textNode: Konva.Text) => {
      // Check write permission first
      if (!checkWritePermission()) {
        return;
      }

      setEditingTextState({ edgeId, textNode });
    },
    [checkWritePermission],
  );

  const handleCloseTextEdit = () => {
    setEditingTextState(null);
  };

  // Pending programmatic edge text edit request (used by EdgeConnector ref handoff)
  const [pendingEdgeEditId, setPendingEdgeEditId] = useState<string | null>(null);

  // Allow external UI (e.g., ShapeToolbar) to start text editing for a node
  useEffect(() => {
    const off = eventBus.on(
      'canvas:text:edit',
      ({ nodeId, field }: { nodeId?: string; field?: 'name' | 'location' | 'text' }) => {
        try {
          const targetId = nodeId || Array.from(selectedIds)[0];
          if (!targetId) return;
          const node = nodes.find((n) => n.id === targetId) as any;
          if (!node) return;
          const isLocked = !!(node?.locked || node?.attrs?.locked);
          if (isLocked) return;
          const kn = nodeRefs.current.get(targetId) as unknown as Konva.Container | undefined;
          if (!kn) return;
          const resolvedField: 'name' | 'location' | 'text' = (() => {
            if (field) return field;
            if (node.type === 'person') return 'name';
            if (typeof node?.attrs?.name === 'string') return 'name';
            // For flowchart-shape, basic-shape, org-shape, use 'text' field
            if (node.type === 'flowchart-shape' || node.type === 'bpmn-shape' || node.type === 'basic-shape' || node.type === 'org-shape')
              return 'text';
            return 'text';
          })();
          let textNode: Konva.Text | null = null;
          // Prefer matching content when available (person name/location)
          try {
            const wantText = node[resolvedField];
            const candidates: Konva.Node[] = (kn as any).find('Text') || [];
            if (Array.isArray(candidates) && candidates.length > 0) {
              if (typeof wantText === 'string' && wantText.length > 0) {
                textNode = (candidates.find((c: any) => (c as Konva.Text).text?.() === wantText) as Konva.Text) || null;
              }
              if (!textNode) textNode = candidates[0] as Konva.Text;
            }
          } catch {}
          if (textNode) setEditingTextState({ nodeId: targetId, field: resolvedField, textNode });
        } catch (err) {
          canvasLogger.error('Failed to start text edit from canvas:text:edit', err);
        }
      },
    );
    return () => off();
  }, [eventBus, nodes, selectedIds, nodeRefs]);

  // Allow external UI (e.g., ConnectorToolbar) to start label editing for an edge
  useEffect(() => {
    const off = eventBus.on(
      'canvas:edge:text:edit',
      ({ edgeId }: { edgeId?: string }) => {
        try {
          const targetId = (() => {
            if (edgeId) return edgeId;
            const first = Array.from(selectedIds)[0];
            const isEdge = edges.some((e) => e.id === first);
            return isEdge ? first : undefined;
          })();
          if (!targetId) return;
          // Signal to EdgeConnector to open using its own ref when ready
          setPendingEdgeEditId(targetId);

          const maxTries = 24; // retry ~1s with 40–50ms spacing
          const attemptOpen = (tries: number) => {
            try {
              const group = edgeRefs.current.get(targetId) as unknown as Konva.Container | undefined;
              if (group && (group as any).find) {
                let textNode: Konva.Text | null = null;
                try {
                  const candidates: Konva.Node[] = (group as any).find('Text') || [];
                  if (Array.isArray(candidates) && candidates.length > 0) {
                    textNode = candidates[0] as Konva.Text;
                  }
                } catch {}
                if (textNode) {
                  handleStartEdgeTextEdit(targetId, textNode);
                  return;
                }
              }
            } catch {}
            if (tries < maxTries) {
              setTimeout(() => attemptOpen(tries + 1), 45);
            }
          };
          attemptOpen(0);
        } catch (err) {
          console.error('Failed to start edge label edit from canvas:edge:text:edit', err);
        }
      },
    );
    return () => off();
  }, [eventBus, edges, selectedIds, edgeRefs, handleStartEdgeTextEdit]);

  // Clear pending request after we actually enter editing for that edge
  useEffect(() => {
    if (editingTextState?.edgeId && pendingEdgeEditId === editingTextState.edgeId) {
      setPendingEdgeEditId(null);
    }
  }, [editingTextState?.edgeId, pendingEdgeEditId]);

  const getInitialEditText = () => {
    // Handle table cell text editing
    if (editingTextState?.nodeId && editingTextState.tableCellKey) {
      if (typeof editingTextState.initialTextOverride === 'string') {
        return editingTextState.initialTextOverride;
      }
      const n = nodes.find((nn) => nn.id === editingTextState.nodeId);
      const attrs = (n?.attrs ?? {}) as Record<string, unknown>;
      const cellText = (attrs.cellText as Record<string, string> | undefined) ?? {};
      return cellText[editingTextState.tableCellKey] ?? '';
    }
    if (editingTextState?.nodeId && editingTextState.field) {
      const node = nodes.find((n) => n.id === editingTextState.nodeId) as any;
      if (!node) return '';
      const attrs = node.attrs || {};
      const props = node.props || {};
      const isOrgPerson =
        node.type === 'org_person' ||
        node.type === 'org_person_photo' ||
        node.type === 'org_person_photo_left' ||
        node.type === 'org_person_photo_right' ||
        node.type === 'org_person_photo_inner_left' ||
        node.type === 'org_person_text';
      const isOrgPosition = node.type === 'org_position';
      const isOrgDepartment = node.type === 'org_department';
      if (isOrgPosition) {
        return (
          (attrs.title as string) ||
          (props.title as string) ||
          (node.text as string) ||
          getOrgChartDefaultPositionTitle()
        );
      }
      if (isOrgPerson) {
        return (
          (attrs.name as string) ||
          (props.name as string) ||
          (node.name as string) ||
          getOrgChartDefaultPersonName()
        );
      }
      if (isOrgDepartment) {
        return (
          (attrs.name as string) ||
          (props.name as string) ||
          (node.name as string) ||
          getOrgChartDefaultDepartmentName()
        );
      }
      return resolveNodeTextFieldValue(node, editingTextState.field);
    }
    if (editingTextState?.edgeId) {
      return (edges.find(e => e.id === editingTextState.edgeId)?.attrs?.label as string) || '';
    }
    return '';
  };

  return {
    editingTextState,
    handleTextEdit,
    handleStartTextEdit,
    handleStartTableCellTextEdit,
    handleStartEdgeTextEdit,
    handleCloseTextEdit,
    getInitialEditText,
    pendingEdgeEditId,
  };
}
